/**
 * Add / edit a chore. Adults only (the Worker re-checks). Rate is typed in dollars
 * and sent as integer cents — money never becomes a float on the way through.
 */
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError, money, parseMoney } from '../../lib/api';
import type { Chore, MemberSummary } from '../../shared/types';
import { Modal, btnPrimary, btnSecondary, cx, fieldInput, fieldLabel } from './shared';

const CADENCES: { value: Chore['cadence']; label: string; hint: string }[] = [
  { value: 'daily', label: 'Daily', hint: 'Can be checked off once a day' },
  { value: 'weekly', label: 'Weekly', hint: 'Can be checked off once a week' },
  { value: 'once', label: 'One time', hint: 'Done once, by whoever gets there first' },
];

export default function ChoreForm({
  chore,
  members,
  onClose,
  onSaved,
}: {
  chore: Chore | null;
  members: MemberSummary[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(chore?.name ?? '');
  const [description, setDescription] = useState(chore?.description ?? '');
  const [rate, setRate] = useState(chore ? (chore.rate / 100).toFixed(2) : '');
  const [assignedTo, setAssignedTo] = useState<string>(
    chore?.assigned_to != null ? String(chore.assigned_to) : ''
  );
  const [cadence, setCadence] = useState<Chore['cadence']>(chore?.cadence ?? 'weekly');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // /chores/members only ever returns active members, so the list is already clean.
  const assignable = members;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the chore a name.');
      return;
    }

    const cents = rate.trim() === '' ? 0 : parseMoney(rate);
    if (cents === null || cents < 0) {
      setError('Enter what it pays as a dollar amount, like 2.50.');
      return;
    }

    const payload = {
      name: trimmed,
      description: description.trim() || null,
      rate: cents,
      assigned_to: assignedTo === '' ? null : Number(assignedTo),
      cadence,
    };

    setSaving(true);
    try {
      if (chore) await api.put<Chore>(`/chores/${chore.id}`, payload);
      else await api.post<Chore>('/chores', payload);
      await onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'That did not save. Try again in a second.'
      );
      setSaving(false);
    }
  }

  const preview = parseMoney(rate.trim() === '' ? '0' : rate);

  return (
    <Modal title={chore ? 'Edit chore' : 'Add a chore'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="chore-name" className={fieldLabel}>
            Name
          </label>
          <input
            id="chore-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldInput}
            placeholder="Take out the trash"
            maxLength={80}
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="chore-desc" className={fieldLabel}>
            What it means <span className="font-normal text-ink-faint">(optional)</span>
          </label>
          <textarea
            id="chore-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={cx(fieldInput, 'min-h-[72px]')}
            placeholder="Kitchen + bathroom bins, out to the curb Sunday night."
            rows={2}
          />
        </div>

        <div>
          <label htmlFor="chore-rate" className={fieldLabel}>
            Pays each time
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
            >
              $
            </span>
            <input
              id="chore-rate"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className={cx(fieldInput, 'pl-7')}
              placeholder="2.00"
              inputMode="decimal"
              aria-describedby="chore-rate-hint"
            />
          </div>
          <p id="chore-rate-hint" className="mt-1 text-xs text-ink-faint">
            {preview !== null && preview > 0
              ? `${money(preview)} lands in their allowance once you approve it.`
              : 'Leave it blank for a chore that pays nothing.'}
          </p>
        </div>

        <div>
          <label htmlFor="chore-assignee" className={fieldLabel}>
            Who does it
          </label>
          <select
            id="chore-assignee"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className={fieldInput}
          >
            <option value="">Anyone in the house</option>
            {assignable.map((m) => (
              <option key={m.id} value={String(m.id)}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <fieldset>
          <legend className={fieldLabel}>How often</legend>
          <div className="mt-2 space-y-2">
            {CADENCES.map((c) => (
              <label
                key={c.value}
                className={cx(
                  'flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border px-3 py-2',
                  cadence === c.value
                    ? 'border-sage bg-sage-soft'
                    : 'border-border bg-surface hover:bg-surface-sunk'
                )}
              >
                <input
                  type="radio"
                  name="cadence"
                  value={c.value}
                  checked={cadence === c.value}
                  onChange={() => setCadence(c.value)}
                  className="h-4 w-4 accent-[var(--color-sage)]"
                />
                <span>
                  <span className="block text-sm font-medium text-ink">{c.label}</span>
                  <span className="block text-xs text-ink-faint">{c.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {error ? (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving} className={cx(btnPrimary, 'flex-1')}>
            {saving ? 'Saving…' : chore ? 'Save changes' : 'Add chore'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
        </div>

        {chore ? (
          <p className="text-xs text-ink-faint">
            Changing the rate only affects chores done from now on. Anything already checked off
            keeps what it was worth at the time.
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
